import React, { useCallback, useEffect, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import { CardElement, useStripe, useElements, Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import axiosInstance from "../../components/content/axiosConfig";
import Preloader from "../../components/custom/preLoader";
import showErrorToast, { showSuccessToast } from "../../components/toast/toast";
import { normalizeCountryCode } from "../../utils/countries";
import { stripeCardElementOptions } from "../../components/custom/StripePaymentSection.jsx";
import { useClearScopedAccountOnMount } from "../../utils/useClearScopedAccountOnMount";

const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ||
    "pk_live_51TcWrnEFp6E1oRogJ6ul5KZwwOONiiQlsq1WfQiz6KFzwtF6k4ktPHC1ykVZiWZd67Wl2S9UA71VlHxMEJBddZmL00RkevipOk"
);

function RenewForm() {
  useClearScopedAccountOnMount();
  const { id } = useParams();
  const history = useHistory();
  const stripe = useStripe();
  const elements = useElements();
  const [packages, setPackages] = useState([]);
  const [fisher, setFisher] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [packageId, setPackageId] = useState("");
  const [pondCount, setPondCount] = useState(1);
  const [country, setCountry] = useState("US");
  const [checkout, setCheckout] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [fisherRes, packagesRes] = await Promise.all([
          axiosInstance.get(`api/company/accounts/${id}`),
          axiosInstance.get("api/Packages/GetAll"),
        ]);
        setFisher(fisherRes.data);
        const list = Array.isArray(packagesRes.data)
          ? packagesRes.data
          : packagesRes.data?.data ?? [];
        const active = list.filter((p) => p.Status && Number(p.Amount) > 0);
        setPackages(active);
        setPackageId(String(fisherRes.data.PackageID || active[0]?.id || ""));
        setPondCount(fisherRes.data.LicensedPondCount || active[0]?.IncludedPonds || 1);
        setCountry(fisherRes.data.Country || "US");
      } catch (e) {
        showErrorToast("Could not load account");
        history.push("/company/accounts");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, history]);

  const startCheckout = useCallback(async () => {
    const res = await axiosInstance.post(`api/company/accounts/${id}/renew-checkout`, {
      packageId: parseInt(packageId, 10),
      pondCount: parseInt(pondCount, 10) || 1,
      country: normalizeCountryCode(country) || "US",
      replaceSubscriptionId: checkout?.subscriptionId || undefined,
    });
    setCheckout(res.data);
    return res.data;
  }, [id, packageId, pondCount, country, checkout?.subscriptionId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    try {
      let checkoutData = checkout;
      if (!checkoutData?.clientSecret) {
        checkoutData = await startCheckout();
      }
      const card = elements.getElement(CardElement);
      const confirmFn =
        checkoutData.clientSecretType === "setup"
          ? stripe.confirmCardSetup
          : stripe.confirmCardPayment;
      const result = await confirmFn(checkoutData.clientSecret, {
        payment_method: { card },
      });
      if (result.error) {
        showErrorToast(result.error.message || "Payment failed");
        setSubmitting(false);
        return;
      }
      const paymentIntentId =
        result.paymentIntent?.id ||
        checkoutData.paymentIntentId ||
        result.setupIntent?.id ||
        checkoutData.setupIntentId;

      await axiosInstance.post(`api/company/accounts/${id}/renew`, {
        PackageID: parseInt(packageId, 10),
        LicensedPondCount: checkoutData.licensedPondCount || parseInt(pondCount, 10) || 1,
        AmountPaid: checkoutData.amount,
        StripePaymentIntentId: paymentIntentId,
        StripeSubscriptionId: checkoutData.subscriptionId,
        Country: normalizeCountryCode(country) || "US",
      });
      showSuccessToast("Subscription updated");
      history.push(`/company/accounts/${id}`);
    } catch (error) {
      showErrorToast(
        error.response?.data?.message || error.response?.data?.error || "Renewal failed"
      );
      setCheckout(null);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !fisher) return <Preloader />;

  return (
    <div className="container-fluid py-3" style={{ maxWidth: 720 }}>
      <Link to={`/company/accounts/${id}`} className="text-muted">
        ← Back
      </Link>
      <h1 className="page-header mt-2">Renew / change package</h1>
      <p className="text-muted">{fisher.FullName} — {fisher.Email}</p>
      <form onSubmit={handleSubmit} className="card p-4">
        <div className="mb-3">
          <label className="form-label">Package</label>
          <select
            className="form-select"
            value={packageId}
            onChange={(e) => {
              setPackageId(e.target.value);
              setCheckout(null);
            }}
          >
            {packages.map((p) => (
              <option key={p.id} value={p.id}>
                {p.Name} — ${p.Amount}
              </option>
            ))}
          </select>
        </div>
        <div className="mb-3">
          <label className="form-label">Licensed ponds</label>
          <input
            type="number"
            min={1}
            className="form-control"
            value={pondCount}
            onChange={(e) => {
              setPondCount(e.target.value);
              setCheckout(null);
            }}
          />
        </div>
        <div className="mb-3 p-3 border rounded">
          <label className="form-label fw-semibold">Card</label>
          <CardElement options={stripeCardElementOptions} />
          {checkout?.recurringLabel ? (
            <div className="form-text mt-2">{checkout.recurringLabel}</div>
          ) : null}
        </div>
        <button type="submit" className="btn btn-primary" disabled={submitting || !stripe}>
          {submitting ? "Processing…" : "Pay & update subscription"}
        </button>
      </form>
    </div>
  );
}

const CompanyAccountRenew = () => (
  <Elements stripe={stripePromise}>
    <RenewForm />
  </Elements>
);

export default CompanyAccountRenew;
